"use client";

import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { useConfig } from "@/components/providers/config";
import { normalizeUrl } from "@/lib/utils";
import type { NavigationLink } from "@/types/storyblok";

export function NavigationLinks({
  navigationType = "header",
  onLinkClick,
}: {
  navigationType?: "header" | "slideover" | "footer";
  onLinkClick?: () => void;
}) {
  const pathname = usePathname();
  const { config } = useConfig();

  const isActive = (href: string) => {
    if (href === "/") {
      return pathname === "/";
    }
    return pathname === href || pathname.startsWith(`${href}/`);
  };

  // Get navigation links from config.header_links
  const links: Array<{ label: string; href: string; _uid: string }> =
    Array.isArray(
      (config as Record<string, unknown>)?.header_links as unknown[] | undefined
    )
      ? (((config as Record<string, unknown>).header_links as unknown[]) ?? [])
          .map((item) => item as NavigationLink)
          .filter((item) => item?.component === "navigationLink")
          .map((item) => {
            const label = item?.link?.title ?? "";
            const href = item?.link?.cached_url ?? item?.link?.url ?? "/";
            return {
              label,
              href: normalizeUrl(href),
              _uid: item?._uid ?? "",
            };
          })
          .filter((l) => l.label && l.href && l._uid)
      : [];

  const handleLinkClick = (link: { label: string; href: string }) => {
    posthog.capture("navigation_link_clicked", {
      link_label: link.label,
      link_href: link.href,
      navigation_type: navigationType,
    });
    // Close slideover when a link is clicked
    if (navigationType === "slideover" && onLinkClick) {
      onLinkClick();
    }
  };

  return (
    <nav
      className={cn(
        navigationType === "header"
          ? "flex items-center gap-8"
          : navigationType === "footer"
            ? "flex flex-wrap gap-4 justify-center md:justify-end lg:gap-8 md:ml-auto"
            : "flex flex-col gap-2 p-4"
      )}
    >
      {links.map((link) => (
        <Button
          key={`${navigationType}-${link._uid}`}
          variant={
            navigationType === "header" || navigationType === "footer"
              ? "link"
              : "ghost"
          }
          size={
            navigationType === "header" || navigationType === "footer"
              ? "nav"
              : "lg"
          }
          className={cn(
            "text-base",
            isActive(link.href) &&
              (navigationType === "header" || navigationType === "footer") &&
              "after:w-full",
            isActive(link.href) &&
              navigationType === "slideover" &&
              "text-secondary"
          )}
          asChild
        >
          <Link href={link.href} onClick={() => handleLinkClick(link)}>
            {link.label}
          </Link>
        </Button>
      ))}
    </nav>
  );
}
